import { useState, useEffect, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VisuallyHidden,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FiInbox,
  FiMail,
  FiMessageCircle,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";

import { useQuery } from "@tanstack/react-query";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import TicketModal from "../components/ticketModal";
import CreateTicketModal from "../components/createTicketModal";
import { useUserData } from "../data/userContext";
import {
  DataTable,
  EmptyState,
  FilterButton,
  MotionReveal,
  PageHeader,
  Pagination,
  SearchField,
  SectionCard,
  StatCard,
  StatusBadge,
  TableSkeleton,
  getStatusMeta,
  toneTokens,
} from "../components/ui";
import { duration, ease, reducedTransition, useReducedMotion } from "../theme";

interface TicketsPageProps {}
type ticketsData = Array<{
  internalId: {
    value: string;
    text: string;
  };
  caseNumber: {
    value: string;
    text: any;
  };
  subject: {
    value: string;
    text: any;
  };
  status: {
    value: string;
    text: string;
  };
  createdDate: {
    value: string;
    text: any;
  };
  closedDate: {
    value: string;
    text: any;
  };
}>;

interface caseData {
  caseNumber: { value: string; text: string | null };
  closedDate: { value: string; text: string | null };
  createdDate: { value: string; text: string | null };
  internalId: { value: string; text: string };
  status: { value: string; text: string };
  subject: { value: string; text: string | null };
}

type TicketFilters = { status: string; dateRange: string };
type FilterKey = keyof TicketFilters;

const TABLE_COLUMNS = ["Case No.", "Subject", "Created", "Closed", "Status", "View"];

/** Status strip chips (§7.3) — values match the popover's <option>s so both drive the same filter. */
const STATUS_CHIPS = [
  { value: "open", label: "Open" },
  { value: "in progress", label: "In progress" },
  { value: "not started", label: "Not started" },
  { value: "closed", label: "Closed" },
] as const;

/** Anything that is not closed counts as open for the help-strip tile. */
const OPEN_STATUSES = ["open", "in progress", "not started"];

/**
 * Presentation-only copy of the page's Time Period predicate (same "DD/MM/YYYY …" parsing as the filter effect),
 * used so the status-chip counts follow the selected period.
 */
const isWithinPeriod = (createdDate: string, dateRange: string) => {
  if (!dateRange) return true;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [datePart] = createdDate.split(" ");
  const [day, month, year] = datePart.split("/");
  const ticketDate = new Date(`${year}-${month}-${day}`);
  switch (dateRange) {
    case "today":
      return ticketDate >= today;
    case "week": {
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      return ticketDate >= weekAgo;
    }
    case "month": {
      const monthAgo = new Date(now);
      monthAgo.setMonth(now.getMonth() - 1);
      return ticketDate >= monthAgo;
    }
    default:
      return true;
  }
};

const pluralise = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

const MotionBox = motion(Box);

/** Count bubble that pops (scale 0.9 → 1) whenever its value changes — keyed remount, gated by reduced motion. */
const CountPop = ({ count, isPressed }: { count: number; isPressed: boolean }) => {
  const reduce = useReducedMotion();
  return (
    <MotionBox
      key={count}
      as="span"
      textStyle="caption"
      bg={isPressed ? "navy.50" : "sand.200"}
      color="text.secondary"
      borderRadius="full"
      px={1.5}
      minW="1.25rem"
      textAlign="center"
      display="inline-block"
      sx={{ fontVariantNumeric: "tabular-nums" }}
      initial={reduce ? false : { scale: 0.9, opacity: 0.6 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={reduce ? reducedTransition : { duration: duration.fast, ease: ease.standard }}
    >
      {count}
    </MotionBox>
  );
};

/** Four pressable status chips above the table. Clicking a pressed chip clears the status (same as "All Statuses"). */
const StatusStrip = ({
  status,
  counts,
  onSelect,
}: {
  status: string;
  counts: Record<string, number>;
  onSelect: (value: string) => void;
}) => (
  <Flex
    role="group"
    aria-label="Filter by status"
    bg="surface.sunken"
    borderRadius="md"
    p={1}
    gap={1}
    wrap="wrap"
    w={{ base: "100%", md: "fit-content" }}
    mb={4}
  >
    {STATUS_CHIPS.map((chip) => {
      const pressed = status.toLowerCase() === chip.value;
      const dot = toneTokens(getStatusMeta("ticket", chip.value).tone).solid;
      return (
        <Button
          key={chip.value}
          variant="segment"
          size="sm"
          // 44px touch target on phones; the compact desktop strip keeps the segmented-control height
          h={{ base: "2.75rem", md: "2.25rem" }}
          flexGrow={{ base: 1, md: 0 }}
          aria-pressed={pressed}
          onClick={() => onSelect(pressed ? "" : chip.value)}
        >
          <HStack as="span" spacing={1.5}>
            <Box as="span" w="6px" h="6px" borderRadius="full" bg={dot} flexShrink={0} aria-hidden />
            <Box as="span">{chip.label}</Box>
            <CountPop count={counts[chip.value] ?? 0} isPressed={pressed} />
          </HStack>
        </Button>
      );
    })}
  </Flex>
);

/** Status / period popover behind the FilterButton. Hoisted out of the page so its open state survives re-renders. */
const FilterPanel = ({
  filters,
  resultCount,
  handleFilterChange,
  onReset,
}: {
  filters: TicketFilters;
  resultCount: number;
  handleFilterChange: (type: FilterKey, value: string) => void;
  onReset: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      closeOnBlur={false} // Prevent closing on blur
    >
      <PopoverTrigger>
        <FilterButton activeCount={activeCount} isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
      </PopoverTrigger>

      {/* Rendered in place (no Portal) so Tab order follows the trigger; closeOnBlur is off, so a portalled panel would strand focus. */}
      <PopoverContent w="320px" maxW="calc(100vw - 2rem)">
        <PopoverHeader>
          <Text textStyle="label" color="text.muted">
            Filter tickets
          </Text>
        </PopoverHeader>
        <PopoverBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel htmlFor="tickets-filter-status">Status</FormLabel>
              <Select
                id="tickets-filter-status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in progress">In Progress</option>
                <option value="closed">Closed</option>
                <option value="not started">Not Started</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="tickets-filter-period">Time Period</FormLabel>
              <Select
                id="tickets-filter-period"
                value={filters.dateRange}
                onChange={(e) => handleFilterChange("dateRange", e.target.value)}
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </Select>
            </FormControl>
          </VStack>
        </PopoverBody>
        <PopoverFooter display="flex" justifyContent="space-between" alignItems="center">
          <Text textStyle="caption" color="text.muted" aria-live="polite">
            {pluralise(resultCount, "result")}
          </Text>
          <Button size="sm" variant="ghost" leftIcon={<FiRotateCcw />} onClick={onReset}>
            Reset
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

const TicketsPage: React.FC<TicketsPageProps> = () => {
  const token = getAuthToken();
  const userData = useUserData();
  const composer = useDisclosure();

  // get user complaints
  const getUserTickets = async () => {
    const { data } = await axiosClient.get("/complaints/listTickets", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data.data;
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["tickets"],
    queryFn: getUserTickets,
  });

  // pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;
  const [filteredTickets, setFilteredTickets] = useState<ticketsData>([]);
  // Mirrors the search field so the empty state can offer "Clear search"
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination logic — clamp so a shrinking list never leaves the view on an empty page
  const pageCount = Math.max(1, Math.ceil(filteredTickets.length / itemsPerPage));
  const currentPage = Math.min(page, pageCount);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Add state for filters
  const [filters, setFilters] = useState({
    status: "",
    dateRange: "",
  });

  // Update filtering logic
  useEffect(() => {
    if (data) {
      let filtered = [...data];

      // Apply status filter if selected
      if (filters.status) {
        filtered = filtered.filter(
          (ticket) => ticket.status.text.toLowerCase() === filters.status.toLowerCase()
        );
      }

      // Filter by date range
      if (filters.dateRange) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        filtered = filtered.filter((ticket) => {
          // Convert the date string to a Date object
          // Assuming createdDate.value is in format "DD/MM/YYYY HH:mm am/pm"
          const [datePart] = ticket.createdDate.value.split(' ');
          const [day, month, year] = datePart.split('/');
          const ticketDate = new Date(`${year}-${month}-${day}`);

          switch (filters.dateRange) {
            case "today":
              return ticketDate >= today;
            case "week":
              const weekAgo = new Date(now);
              weekAgo.setDate(now.getDate() - 7);
              return ticketDate >= weekAgo;
            case "month":
              const monthAgo = new Date(now);
              monthAgo.setMonth(now.getMonth() - 1);
              return ticketDate >= monthAgo;
            default:
              return true;
          }
        });
      }

      setFilteredTickets(filtered);
      setPage(1); // Reset to first page when filters change
    }
  }, [data, filters]);

  const searchTickets = (tickets: ticketsData, query: string) => {
    const lowerCaseQuery = query.toLowerCase();

    return tickets.filter((ticket) =>
      ticket.caseNumber.value.toLowerCase().includes(lowerCaseQuery) ||
      ticket.subject.value.toLowerCase().includes(lowerCaseQuery) ||
      ticket.status.text.toLowerCase().includes(lowerCaseQuery)
    );
  };

  // Update handleSearch to use correct property paths
  const handleSearch = (e: any) => {
    const query = e.target.value;
    setPage(1); // A new search always starts from the first page
    if (query === "") {
      // Reapply filters without search
      let filtered = [...data];
      setFilteredTickets(filtered);
    } else {
      // Search within already filtered results
      const searchResults = searchTickets(data, query);
      setFilteredTickets(searchResults);
    }
  };

  // Handle filter changes without closing popover
  const handleFilterChange = (type: 'status' | 'dateRange', value: string) => {
    setFilters(prev => ({ ...prev, [type]: value }));
    // Don't close popover when changing filters
  };

  const resetFilters = () => {
    setFilters({ status: "", dateRange: "" });
  };

  // The filter effect rebuilds the list from `data` alone. When it re-runs (filters cleared by a new search, or a
  // background refetch) re-apply a live search on top so the field and the table never disagree. Declared after the
  // filter effect, so it runs after it in the same commit. Handlers above are unchanged.
  useEffect(() => {
    if (searchQuery) handleSearch({ target: { value: searchQuery } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filters]);

  // Presentation-only derived values
  const allTickets: ticketsData = data ?? [];
  const rows: caseData[] = currentItems ?? [];
  const hasActiveFilters = Object.values(filters).some((v) => v);
  const hasSearch = searchQuery !== "";
  // With no search and no filter the effect copies `data` into `filteredTickets`; an empty list alongside non-empty
  // data means that effect has not synced yet (first paint after the query resolves) — keep the skeleton up.
  const awaitingSync = allTickets.length > 0 && filteredTickets.length === 0 && !hasSearch && !hasActiveFilters;
  const isFirstLoad = (isPending && !filteredTickets.length) || awaitingSync;
  // The empty-state reset buttons unmount themselves once rows reappear — hand focus back to the search field.
  const searchRef = useRef<HTMLInputElement>(null);
  // Search and the status/period filters are applied by separate handlers that each start from the full list, so
  // they are kept mutually exclusive here: the pressed chips / filter count never claim a filter the table ignores.
  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value !== "" && hasActiveFilters) resetFilters(); // the effect above re-applies the search afterwards
    // handleSearch reads e.target.value — adapt the primitive's string callback to it unchanged.
    handleSearch({ target: { value } });
  };
  const applyFilter = (type: FilterKey, value: string) => {
    if (hasSearch) setSearchQuery("");
    handleFilterChange(type, value);
  };
  const clearFilters = () => {
    if (hasSearch) setSearchQuery("");
    resetFilters();
  };
  // Chip counts follow the Time Period filter (but not the status filter, so every chip keeps its own count).
  const periodTickets = filters.dateRange
    ? allTickets.filter((t) => isWithinPeriod(t.createdDate?.value ?? "", filters.dateRange))
    : allTickets;
  const statusCounts = STATUS_CHIPS.reduce<Record<string, number>>((acc, chip) => {
    acc[chip.value] = periodTickets.filter((t) => (t.status?.text ?? "").toLowerCase() === chip.value).length;
    return acc;
  }, {});
  // Without data a failed load must not read as a confident "0 open tickets".
  const ticketsFailed = isError && !data;
  const openCount = allTickets.filter((t) => OPEN_STATUSES.includes((t.status?.text ?? "").toLowerCase())).length;
  const bdeName = userData?.bde.value;
  const bdeEmail = userData?.bdeEmail.value;

  const newTicketButton = (onOpen: () => void, size?: "sm") => (
    <Button variant="primary" size={size} leftIcon={<FiPlus />} onClick={onOpen}>
      New ticket
    </Button>
  );

  return (
    <Stack spacing={6}>
      <PageHeader
        variant="strip"
        eyebrow="HELP & SUPPORT"
        title="We're here to help"
        actions={
          <CreateTicketModal
            triggerRefetch={refetch}
            disclosure={composer}
            renderTrigger={(onOpen) => newTicketButton(onOpen)}
          />
        }
      />

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
        <MotionReveal delay={0.04}>
          <StatCard
            variant="action"
            tone="brand"
            icon={FiMessageCircle}
            label="Raise a ticket"
            value="We reply within one working day"
            valueTextStyle="h4"
            caption="Tell us what happened"
            onClick={composer.onOpen}
            h="full"
          />
        </MotionReveal>
        <MotionReveal delay={0.08}>
          <StatCard
            tone="info"
            icon={FiMail}
            label="Contact your BDE"
            value={bdeName || "—"}
            valueTextStyle="h3"
            caption={
              bdeEmail ? (
                <Link
                  href={`mailto:${bdeEmail}`}
                  color="text.link"
                  fontWeight={600}
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  wordBreak="break-all"
                  _hover={{ color: "navy.600", textDecoration: "underline" }}
                  _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
                >
                  <Icon as={FiMail} boxSize="14px" aria-hidden />
                  {bdeEmail}
                </Link>
              ) : (
                "Your account team will be in touch"
              )
            }
            h="full"
          />
        </MotionReveal>
        <MotionReveal delay={0.12}>
          <StatCard
            tone={ticketsFailed ? "neutral" : openCount > 0 ? "info" : "success"}
            icon={FiInbox}
            label="Open tickets"
            value={ticketsFailed ? "—" : openCount}
            caption={ticketsFailed ? "Couldn't load tickets" : "Across all branches"}
            isLoading={isFirstLoad}
            h="full"
          />
        </MotionReveal>
      </SimpleGrid>

      <MotionReveal delay={0.16}>
        <SectionCard
          folio="01"
          title="Case history"
          lede={isFirstLoad || (isError && !data) ? undefined : pluralise(allTickets.length, "ticket")}
          isLoading={isFirstLoad}
          toolbar={
            <>
              <SearchField
                ref={searchRef}
                value={searchQuery}
                onChange={onSearchChange}
                placeholder="Search tickets"
                aria-label="Search tickets"
                isDisabled={!data}
              />
              <FilterPanel
                filters={filters}
                resultCount={filteredTickets.length}
                handleFilterChange={applyFilter}
                onReset={clearFilters}
              />
            </>
          }
        >
          {isFirstLoad ? (
            <TableSkeleton columns={TABLE_COLUMNS} rows={5} />
          ) : isError && !data ? (
            <Alert status="error" alignItems="flex-start">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>We couldn't load your tickets</AlertTitle>
                <AlertDescription>Check your connection and try again.</AlertDescription>
              </Box>
              <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          ) : allTickets.length === 0 ? (
            <EmptyState
              icon={FiInbox}
              title="No tickets yet — raise your first one"
              description="Tell us what happened and we will reply within one working day."
              action={newTicketButton(composer.onOpen, "sm")}
            />
          ) : (
            <>
              <StatusStrip
                status={filters.status}
                counts={statusCounts}
                onSelect={(value) => applyFilter("status", value)}
              />

              {rows.length === 0 ? (
                <EmptyState
                  icon={FiSearch}
                  title="No matching tickets"
                  description="Try another search term or clear the filters."
                  action={
                    hasSearch || hasActiveFilters ? (
                      <HStack spacing={2} justify="center" wrap="wrap">
                        {hasSearch && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<FiRotateCcw />}
                            onClick={() => {
                              onSearchChange("");
                              searchRef.current?.focus();
                            }}
                          >
                            Clear search
                          </Button>
                        )}
                        {hasActiveFilters && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<FiRotateCcw />}
                            onClick={() => {
                              clearFilters();
                              searchRef.current?.focus();
                            }}
                          >
                            Clear filters
                          </Button>
                        )}
                      </HStack>
                    ) : undefined
                  }
                  py={10}
                />
              ) : (
                <>
                  <DataTable label="Tickets table">
                    <Table>
                      <Thead>
                        <Tr>
                          {TABLE_COLUMNS.map((column) => (
                            <Th key={column} scope="col">
                              {column}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {rows.map((caseItem: caseData) => (
                          <Tr key={caseItem.internalId.value}>
                            <Td>
                              <Text as="span" textStyle="numeral" fontWeight={700}>
                                {caseItem.caseNumber.value}
                              </Text>
                            </Td>
                            <Td minW="14rem" maxW="26rem">
                              <Text
                                textStyle="body.sm"
                                fontWeight={600}
                                noOfLines={1}
                                whiteSpace="normal"
                                title={caseItem.subject.value ?? undefined}
                              >
                                {caseItem.subject.value}
                              </Text>
                            </Td>
                            <Td>
                              <Text as="span" textStyle="numeral">
                                {caseItem.createdDate.value}
                              </Text>
                            </Td>
                            <Td>
                              {caseItem.closedDate.value ? (
                                <Text as="span" textStyle="numeral">
                                  {caseItem.closedDate.value}
                                </Text>
                              ) : (
                                <>
                                  <Text as="span" color="text.muted" aria-hidden>
                                    —
                                  </Text>
                                  <VisuallyHidden>Not closed</VisuallyHidden>
                                </>
                              )}
                            </Td>
                            <Td>
                              <StatusBadge kind="ticket" value={caseItem.status.text} />
                            </Td>
                            <Td>
                              <TicketModal
                                ticketId={parseInt(caseItem.internalId.value)}
                                caseNumber={caseItem.caseNumber.value}
                                subject={caseItem.subject.value}
                                createdDate={caseItem.createdDate.value}
                                closedDate={caseItem.closedDate.value}
                                status={caseItem.status.text}
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </DataTable>
                  <Pagination
                    page={currentPage}
                    pageSize={itemsPerPage}
                    total={filteredTickets?.length || 0}
                    onChange={setPage}
                    itemLabel="tickets"
                  />
                </>
              )}
            </>
          )}
        </SectionCard>
      </MotionReveal>
    </Stack>
  );
};

export default TicketsPage;
